import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Stack,
  Popover,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import DownloadIcon from "@mui/icons-material/Download";
import MoreVertOutlinedIcon from "@mui/icons-material/MoreVertOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  deleteAttachment,
  getAttachmentBlob,
  getTaskAttachments,
} from "../../services/attachmentService";
import { getProjectAttachments } from "../../services/projectService";
import { downloadBlob } from "../../utils/apiHelpers";
import AttachmentForm from "../Forms/AttachmentForm";
import ConfirmDialog from "../Common/ConfirmDialog";
import Notification from "../Common/Notification";
import { useTranslation } from "react-i18next";
import useAcl from "../../hooks/useAcl";
import useEntityPermissions from "../../hooks/useEntityPermissions";

export interface Attachment {
  id: string;
  name: string;
  url: string;
  createdAt?: string;
  createdById?: string;
}

interface Props {
  parentId: string;
  onRefresh: () => Promise<void>;
  setNotification: (type: "success" | "error", message: string) => void;
  parentType?: "Task" | "Project" | "ProjectTask";
}

const AttachmentsAccordion: React.FC<Props> = ({
  parentId,
  onRefresh,
  setNotification,
  parentType = "Task",
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<string | null>(
    null
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedAttachmentId, setSelectedAttachmentId] = useState<
    string | null
  >(null);
  const [loading, setLoading] = useState(false);
  const [downloadingAttachmentId, setDownloadingAttachmentId] = useState<
    string | null
  >(null);
  const [downloadNotification, setDownloadNotification] = useState<{
    open: boolean;
    fileUrl: string;
    fileName: string;
  } | null>(null);
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const { canCreate } = useAcl("Attachment");
  const attachmentPermissions = useEntityPermissions("Attachment", attachments);

  const loadAttachments = useCallback(async () => {
    if (!parentId || parentId === "new" || parentId === "temp") {
      const pendingAttachments: Attachment[] = JSON.parse(
        localStorage.getItem("pendingAttachments") || "[]"
      );
      setAttachments(pendingAttachments);
      return;
    }
    try {
      let loadedAttachments: Attachment[] = [];
      if (parentType === "Project") {
        loadedAttachments = await getProjectAttachments(parentId);
      } else {
        loadedAttachments = await getTaskAttachments(parentId, parentType);
      }
      setAttachments(loadedAttachments);
    } catch (err) {
      console.error("Ошибка при загрузке вложений:", err);
      setAttachments([]);
    }
  }, [parentId, parentType]);

  useEffect(() => {
    loadAttachments();
  }, [loadAttachments]);

  const handleDelete = (id: string) => {
    setAttachmentToDelete(id);
    setConfirmOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmDelete = async () => {
    if (attachmentToDelete) {
      setLoading(true);
      try {
        if (parentId === "new" || parentId === "temp") {
          const updatedAttachments = attachments.filter(
            (attachment) => attachment.id !== attachmentToDelete
          );
          localStorage.setItem(
            "pendingAttachments",
            JSON.stringify(updatedAttachments)
          );
          await onRefresh();
        } else {
          await deleteAttachment(parentId, attachmentToDelete, parentType);
          await onRefresh();
          await loadAttachments();
        }
        setNotification("success", t("attachments.deleteSuccess"));
        setTimeout(() => {
          setConfirmOpen(false);
          setAttachmentToDelete(null);
        }, 1000);
      } catch {
        setNotification("error", t("attachments.deleteError"));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleCancelDelete = () => {
    setConfirmOpen(false);
    setAttachmentToDelete(null);
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLElement>,
    attachmentId: string
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedAttachmentId(attachmentId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedAttachmentId(null);
  };


  const handleCreated = async (attachment: Attachment, file?: File) => {
    await onRefresh();
    await loadAttachments();
    setFormOpen(false);
    if (!file) {
      setNotification("success", t("attachmentForm.renameSuccess"));
    } else {
      setNotification("success", t("attachmentForm.uploadSuccess"));
    }
  };

  const handleDownload = async (attachmentId: string, fileName: string) => {
    setDownloadingAttachmentId(attachmentId);
    try {
      const blob = await getAttachmentBlob(attachmentId);
      downloadBlob(blob, fileName);
      const fileUrl = window.URL.createObjectURL(blob);
      setDownloadNotification({
        open: true,
        fileUrl,
        fileName,
      });
    } catch (error) {
      console.error("Ошибка при скачивании файла:", error);
      setNotification("error", t("attachmentForm.uploadError"));
    } finally {
      setDownloadingAttachmentId(null);
    }
  };

  const handleCloseDownloadNotification = () => {
    if (downloadNotification?.fileUrl) {
      window.URL.revokeObjectURL(downloadNotification.fileUrl);
    }
    setDownloadNotification(null);
  };

  const handleOpenDownloadedFile = () => {
    if (downloadNotification?.fileUrl) {
      window.open(downloadNotification.fileUrl, "_blank");
      handleCloseDownloadNotification();
    }
  };

  return (
    <Box
      sx={{
        borderRadius: 2,
        background: "#fff",
        mb: 2,
        minWidth: 280,
        maxWidth: 500,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          justifyContent: "space-between",
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Typography
            variant="h4"
            sx={{
              flex: 1,
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            {t("attachments.title")}
          </Typography>
          <IconButton
            size="small"
            sx={{
              transition: "transform 0.2s",
              mr: 1,
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              color: "grey.700",
            }}
            disableRipple
            tabIndex={-1}
            disabled={loading}
          >
            <ExpandMoreIcon />
          </IconButton>
        </Box>
        {canCreate && (
          <IconButton
            size="small"
            color="primary"
            onClick={(e) => {
              e.stopPropagation();
              setFormOpen(true);
            }}
            sx={{
              border: "1px solid #4467A5",
              borderRadius: "8px",
              bgcolor: "#4467A5",
              color: "#fff",
              "&:hover": { bgcolor: "#385894" },
            }}
            disabled={loading}
          >
            <AddIcon />
          </IconButton>
        )}
      </Box>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ mt: 1.5 }}>
          {attachments.length === 0 ? (
            <Typography
              color="text.secondary"
              sx={{ fontSize: "0.95rem", py: 2, textAlign: "center" }}
            >
              {t("attachments.empty")}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {attachments.map((attachment) => {
                const { canEdit, canDelete } = attachmentPermissions[
                  attachment.id
                ] || {
                  canEdit: false,
                  canDelete: false,
                };
                return (
                  <Box
                    key={attachment.id}
                    sx={{
                      border: "1px solid",
                      borderColor: "grey.300",
                      borderRadius: 1,
                      p: 1,
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: { xs: "flex-start", sm: "center" },
                      gap: 1,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: { xs: "0.8rem", sm: "0.875rem" },
                        fontWeight: "medium",
                      }}
                    >
                      {attachment.name}
                    </Typography>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleDownload(attachment.id, attachment.name)
                        }
                        sx={{ p: { xs: 1, sm: 1.5 } }}
                        disabled={loading || downloadingAttachmentId === attachment.id}
                      >
                        {downloadingAttachmentId === attachment.id ? (
                          <CircularProgress size={20} />
                        ) : (
                          <DownloadIcon fontSize="small" />
                        )}
                      </IconButton>
                      {(canEdit || canDelete) && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleOpenMenu(e, attachment.id)}
                          sx={{ p: { xs: 1, sm: 1.5 } }}
                          disabled={loading}
                        >
                          <MoreVertOutlinedIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Stack>
          )}
        </Box>
      </Collapse>
      {/* Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        {selectedAttachmentId &&
          (() => {
            const attachment = attachments.find(
              (a) => a.id === selectedAttachmentId
            );
            if (!attachment) return null;
            const { canEdit, canDelete } = attachmentPermissions[
              selectedAttachmentId
            ] || {
              canEdit: false,
              canDelete: false,
            };
            return (
              <>
                {canDelete && (
                  <MenuItem onClick={() => handleDelete(selectedAttachmentId)}>
                    <ListItemIcon>
                      <DeleteIcon fontSize="small" sx={{ color: "#D32F2F" }} />
                    </ListItemIcon>
                    <ListItemText primary={t("attachments.deleteButton")} />
                  </MenuItem>
                )}
              </>
            );
          })()}
      </Popover>
      
      <AttachmentForm
        open={formOpen}
        parentId={parentId}
        onClose={() => {
          setFormOpen(false);
        }}
        onCreated={handleCreated}
        attachment={attachments.find((a) => a.id === selectedAttachmentId)}
        parentType={parentType}
        existingAttachments={attachments}
      />
      <ConfirmDialog
        open={confirmOpen}
        title={t("attachments.deleteConfirmTitle")}
        content={t("attachments.deleteConfirmContent")}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        loading={loading}
      />
      {downloadNotification && (
        <Notification
          open={downloadNotification.open}
          message={t("attachmentForm.downloadSuccess")}
          severity="success"
          onClose={handleCloseDownloadNotification}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          action={{
            label: t("attachmentForm.openFile"),
            onClick: handleOpenDownloadedFile,
          }}
        />
      )}
    </Box>
  );
};

export default AttachmentsAccordion;
